import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

/**
 * The four flag value types supported by Kinde:
 * Boolean, String, Integer, JSON
 */
export type FlagValue =
	| boolean
	| null
	| number
	| Record<string, unknown>
	| string;

/** Shape of the object returned by Kinde's `getFlag`. */
interface KindeFlagResult {
	code: string;
	is_default: boolean;
	type: string;
	value: FlagValue;
}

/** Type guard that narrows an unknown Kinde response to `KindeFlagResult`. */
const isKindeFlagResult = (v: unknown): v is KindeFlagResult =>
	typeof v === "object" && v !== null && "value" in v;

/**
 * Extract a typed value from Kinde's getFlag response.
 * Kinde returns `{ code, type, value, is_default }` for flags.
 */
const extractFlagValue = (flagResult: unknown): FlagValue => {
	if (flagResult === null || flagResult === undefined) return null;
	if (isKindeFlagResult(flagResult)) return flagResult.value ?? null;
	if (
		typeof flagResult === "boolean" ||
		typeof flagResult === "number" ||
		typeof flagResult === "string"
	)
		return flagResult;
	return null;
};

/** Stable React Query cache key scoped to the current user and flag name. */
const flagQueryKey = (userId: string | undefined, flagName: string) =>
	["kinde-feature-flag", userId, flagName] as const;

/**
 * Low-level hook that fetches feature flags from Kinde and exposes the raw
 * query state. Prefer {@link useFlag} for component-level usage — it returns
 * `enabled`/`disabled` which handle loading state implicitly and match the
 * `allowed`/`denied` ergonomics of `usePermissions`.
 *
 * Use `useFeatureFlags` directly only when you need to fetch or read multiple
 * flags in one call, or when you need the raw `getFlag`/`fetchFlag` API.
 *
 * `staleTime: 0` and `gcTime: 0` are intentional — Kinde flag reads are
 * synchronous token reads (not network requests), so caching is not needed
 * and would risk serving stale values after a token refresh.
 *
 * @param flagNames - Flag names to fetch automatically on mount
 */
export const useFeatureFlags = (flagNames?: string[]) => {
	const {
		getFlag: kindeGetFlag,
		isAuthenticated,
		isLoading: isAuthLoading,
		user,
	} = useKindeAuth();
	const queryClient = useQueryClient();

	// Parallel queries for each flag declared at hook initialisation time.
	const flagQueries = useQueries({
		queries: (flagNames ?? []).map((name) => ({
			enabled: isAuthenticated && !isAuthLoading,
			gcTime: 0,
			queryFn: async (): Promise<FlagValue> => {
				const result = await kindeGetFlag(name);
				return extractFlagValue(result);
			},
			queryKey: flagQueryKey(user?.id, name),
			staleTime: 0,
		})),
	});

	// Build a name → value map from settled query results for fast synchronous lookup.
	const flags = useMemo(() => {
		const map = new Map<string, FlagValue>();
		(flagNames ?? []).forEach((name, i) => {
			const query = flagQueries[i];
			if (query && query.data !== undefined) {
				map.set(name, query.data);
			}
		});
		return map;
	}, [flagNames, flagQueries]);

	const isLoading = isAuthLoading || flagQueries.some((q) => q.isLoading);
	const error = flagQueries.find((q) => q.error)?.error ?? null;

	const refetch = useCallback(async () => {
		await Promise.all(flagQueries.map((q) => q.refetch()));
	}, [flagQueries]);

	/**
	 * Imperatively fetch a single flag and write it into the query cache.
	 * Safe to call even if the flag was not declared in `flagNames`.
	 * Returns `null` when the user is not yet authenticated.
	 */
	const fetchFlag = useCallback(
		async (flagName: string): Promise<FlagValue> => {
			if (!isAuthenticated || isAuthLoading) return null;
			return queryClient.fetchQuery({
				gcTime: 0,
				queryFn: async (): Promise<FlagValue> => {
					const result = await kindeGetFlag(flagName);
					return extractFlagValue(result);
				},
				queryKey: flagQueryKey(user?.id, flagName),
				staleTime: 0,
			});
		},
		[queryClient, kindeGetFlag, isAuthenticated, isAuthLoading, user?.id],
	);

	/** Convenience wrapper that fetches multiple flags concurrently. */
	const fetchFlags = useCallback(
		async (names: string[]) => {
			if (!isAuthenticated || isAuthLoading) return;
			await Promise.all(names.map(fetchFlag));
		},
		[fetchFlag, isAuthenticated, isAuthLoading],
	);

	/**
	 * Synchronously read a flag value from the local cache.
	 * Checks the `flags` map first, then falls back to the raw React Query cache
	 * for flags fetched on-demand. Returns `defaultValue` when no cached entry exists.
	 */
	const getFlag = useCallback(
		<T extends FlagValue = FlagValue>(
			flagName: string,
			defaultValue: null | T = null,
		): null | T => {
			// Check the flags Map first (populated from flagNames provided at init)
			if (flags.has(flagName)) {
				const value = flags.get(flagName);
				return value === null ? defaultValue : (value as T);
			}

			// Fall back to query cache for flags fetched on-demand via fetchFlag
			const cached = queryClient.getQueryData<FlagValue>(
				flagQueryKey(user?.id, flagName),
			);
			if (cached !== undefined) {
				return cached === null ? defaultValue : (cached as T);
			}

			return defaultValue;
		},
		[flags, queryClient, user?.id],
	);

	return { error, fetchFlag, fetchFlags, flags, getFlag, isLoading, refetch };
};

/**
 * Typed registry of every feature flag name used in the dashboard.
 * Use these constants instead of raw strings to get autocomplete and
 * catch renames at compile time.
 */
export const Flags = {
	UserDelete: "user:delete",
} as const;

/**
 * Return type for {@link useFlag}.
 *
 * Mirrors the `allowed`/`denied` pattern from `usePermissions` so that loading
 * state is handled implicitly — both `enabled` and `disabled` are `false` while
 * the flag is still being fetched, keeping UI stable without callers having to
 * manually check `isLoading`.
 *
 * `enabled` — resolved AND value is truthy.
 *   Use when you need a positive guard:
 *   - Gating a feature: `{enabled && <FeatureUI />}`
 *   - Disabling a control during load: `isDisabled={!enabled}`
 *
 * `disabled` — resolved AND value is falsy.
 *   Use when you only want to show fallback/support UI once denial is confirmed,
 *   to avoid briefly flashing it for users who do have the flag on:
 *   - `{disabled && <SupportButton />}`
 *   - `{disabled ? <ComingSoon /> : null}`
 *
 * `value` — the raw resolved value. Useful for non-boolean flags (string, number).
 */
export interface UseFlagResult<T extends FlagValue = boolean> {
	/** Resolved AND value is falsy. Both `enabled` and `disabled` are `false` while loading. */
	disabled: boolean;
	/** Resolved AND value is truthy. Both `enabled` and `disabled` are `false` while loading. */
	enabled: boolean;
	/** Raw resolved value, or `null` while loading. Useful for string/number flags. */
	value: null | T;
}

/**
 * Convenience hook for reading a single feature flag.
 * Mirrors the `allowed`/`denied` ergonomics of `usePermissions`.
 *
 * @example
 * const { enabled: canFilter } = useFlag(Flags.EventsFilterSensors);
 * const { enabled, disabled } = useFlag(Flags.LocationUpdateAddress);
 * const { value: theme } = useFlag<string>(Flags.Theme, 'light');
 */
export const useFlag = <T extends FlagValue = boolean>(
	flagName: string,
	defaultValue: null | T = null,
): UseFlagResult<T> => {
	// Stable array reference so the inner useMemo in useFeatureFlags doesn't
	// recompute on every render when flagName hasn't changed.
	const flagNames = useMemo(() => [flagName], [flagName]);
	const { getFlag, isLoading } = useFeatureFlags(flagNames);
	const value = getFlag<T>(flagName, defaultValue);
	// Use !isLoading (not `value !== null`) as the resolved signal so that flags
	// which are unconfigured in Kinde (value = null) are still treated as
	// resolved-falsy — disabled=true, enabled=false — rather than being stuck
	// in an unresolved state where both are permanently false.
	return {
		disabled: !isLoading && !value,
		enabled: !isLoading && !!value,
		value,
	};
};
